import {
  AUTH_USER_TOKEN_KEY,
  AUTH_USER_TOKEN_KEY_LEGACY,
  AUTH_ACCESS_TOKEN_KEY,
  AUTH_REFRESH_TOKEN_KEY,
} from "./constants";

const AUTH_LAUNCH_CONTEXT_KEY = "AUTH_LAUNCH_CONTEXT";

// ---------------------------------------------------------------------------
// Legacy token migration (runs once on module load)
// ---------------------------------------------------------------------------
try {
  const legacyToken = window.localStorage.getItem(AUTH_USER_TOKEN_KEY_LEGACY);
  if (legacyToken && legacyToken !== "undefined" && legacyToken !== "null") {
    if (!window.localStorage.getItem(AUTH_USER_TOKEN_KEY)) {
      window.localStorage.setItem(AUTH_USER_TOKEN_KEY, legacyToken);
    }
    window.localStorage.removeItem(AUTH_USER_TOKEN_KEY_LEGACY);
  }
  const oldRefresh = window.localStorage.getItem("refreshToken");
  if (oldRefresh && oldRefresh !== "undefined" && oldRefresh !== "null") {
    if (!window.localStorage.getItem(AUTH_REFRESH_TOKEN_KEY)) {
      window.localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, oldRefresh);
    }
    window.localStorage.removeItem("refreshToken");
  }
} catch (_e) {}

// ---------------------------------------------------------------------------
// JWT parsing
// ---------------------------------------------------------------------------
export const parseJwtPayload = (token) => {
  try {
    const payloadPart = String(token || "").split(".")[1];
    if (!payloadPart) return null;
    const padded = payloadPart
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(payloadPart.length + ((4 - (payloadPart.length % 4)) % 4), "=");
    return JSON.parse(window.atob(padded));
  } catch (_error) {
    return null;
  }
};

const getTokenEmail = (payload) => {
  if (!payload) return null;
  return (
    payload.email ||
    payload.preferred_username ||
    payload.upn ||
    payload.unique_name ||
    null
  );
};

const getTokenUserId = (payload) => {
  if (!payload) return null;
  return (
    payload["cognito:username"] ||
    payload.sub ||
    payload.oid ||
    getTokenEmail(payload) ||
    payload.username ||
    null
  );
};

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------
export const getStoredAuthToken = () => {
  try {
    const token = window.localStorage.getItem(AUTH_USER_TOKEN_KEY);
    return token && token !== "undefined" && token !== "null" ? token : null;
  } catch (_error) {
    return null;
  }
};

export const getStoredRefreshToken = () => {
  try {
    return window.localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  } catch (_error) {
    return null;
  }
};

export const hasStoredAuthToken = () => Boolean(getStoredAuthToken());

export const clearAuthStorage = () => {
  try {
    window.localStorage.removeItem(AUTH_USER_TOKEN_KEY);
    window.localStorage.removeItem(AUTH_USER_TOKEN_KEY_LEGACY);
    window.localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    window.localStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
    window.localStorage.removeItem("oauth_provider");
    window.localStorage.removeItem("LOGIN_ACTION");
    window.localStorage.removeItem(AUTH_LAUNCH_CONTEXT_KEY);
    window.localStorage.removeItem("cs_selected_domain");
  } catch (_error) {}
};

// ---------------------------------------------------------------------------
// Token profile extraction
// ---------------------------------------------------------------------------
export const getAuthEmailFromToken = () => {
  const token = getStoredAuthToken();
  if (!token) return null;
  return getTokenEmail(parseJwtPayload(token));
};

export const getAuthUserIdFromToken = () => {
  const token = getStoredAuthToken();
  if (!token) return null;
  return getTokenUserId(parseJwtPayload(token));
};

export const getAuthProfileFromToken = () => {
  const token = getStoredAuthToken();
  if (!token) return null;
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  return {
    email: getTokenEmail(payload),
    preferredUsername: payload.preferred_username || payload.upn || null,
    name: payload.name || payload.unique_name || null,
    givenName: payload.given_name || null,
    familyName: payload.family_name || null,
    userId: getTokenUserId(payload),
  };
};

// ---------------------------------------------------------------------------
// Token expiry
// ---------------------------------------------------------------------------
export const isTokenExpired = (bufferSeconds = 60) => {
  const token = getStoredAuthToken();
  if (!token) return true;
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp - nowSeconds < bufferSeconds;
};

// ---------------------------------------------------------------------------
// Token refresh via Cognito
// ---------------------------------------------------------------------------
export const refreshAuthToken = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const cognitoDomain = process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID;
    if (!cognitoDomain || !clientId) return null;

    const body = new URLSearchParams();
    body.set("grant_type", "refresh_token");
    body.set("client_id", clientId);
    body.set("refresh_token", refreshToken);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    const response = await fetch(`https://${cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
      signal: controller.signal,
    }).finally(() => window.clearTimeout(timeoutId));

    if (!response.ok) return null;

    const data = await response.json();
    if (!data.id_token) return null;

    window.localStorage.setItem(AUTH_USER_TOKEN_KEY, data.id_token);
    if (data.access_token) {
      window.localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, data.access_token);
    }
    return data.id_token;
  } catch (_error) {
    return null;
  }
};

let _refreshPromise = null;

export const getValidToken = async () => {
  let token = getStoredAuthToken();
  if (token && isTokenExpired(120)) {
    if (!_refreshPromise) {
      _refreshPromise = refreshAuthToken().finally(() => {
        _refreshPromise = null;
      });
    }
    const newToken = await _refreshPromise;
    if (newToken) token = newToken;
  }
  return token || null;
};

// ---------------------------------------------------------------------------
// Redirect host validation
// ---------------------------------------------------------------------------
const ALLOWED_REDIRECT_DOMAINS = ["yourrighthand.ai", "oelement.ai", "oelement.net", "localhost"];

const stripUrlToAuthority = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split("/")[0]
    .replace(/^www\./, "");
};

const normalizeHost = (value) => {
  return stripUrlToAuthority(value).split(":")[0];
};

const isLocalHost = (value) => {
  const host = normalizeHost(value);
  return host === "localhost" || host === "127.0.0.1";
};

const normalizeRedirectSource = (value) => {
  const authority = stripUrlToAuthority(value);
  if (!authority) return "";
  return isLocalHost(authority) ? authority : normalizeHost(authority);
};

const normalizeTenant = (value) => {
  const tenant = String(value || "").trim().toLowerCase();
  return tenant === "cre" || tenant === "spark" ? tenant : "";
};

export const isAllowedRedirectHost = (host) => {
  const h = normalizeHost(host);
  if (!h) return false;
  return ALLOWED_REDIRECT_DOMAINS.some((d) => h === d || h.endsWith(`.${d}`));
};

const readStoredLaunchContext = () => {
  try {
    const raw = window.localStorage.getItem(AUTH_LAUNCH_CONTEXT_KEY);
    if (!raw) return { tenant: "", from: "" };
    const parsed = JSON.parse(raw);
    return {
      tenant: normalizeTenant(parsed?.tenant),
      from: normalizeRedirectSource(parsed?.from),
    };
  } catch (_error) {
    return { tenant: "", from: "" };
  }
};

const writeStoredLaunchContext = (context) => {
  try {
    window.localStorage.setItem(AUTH_LAUNCH_CONTEXT_KEY, JSON.stringify(context));
  } catch (_error) {}
};

// ---------------------------------------------------------------------------
// Launch context (tenant / from)
// ---------------------------------------------------------------------------
export const persistLaunchContextFromLocation = (locationLike = window.location) => {
  if (typeof window === "undefined") return { tenant: "", from: "" };

  const params = new URLSearchParams(locationLike?.search || "");
  const tenantFromQuery = normalizeTenant(params.get("tenant"));
  const fromFromQuery = normalizeRedirectSource(params.get("from"));
  const stored = readStoredLaunchContext();

  const tenant = tenantFromQuery || stored.tenant || "";
  const from = fromFromQuery || stored.from || "";

  const context = { tenant, from };
  if (context.tenant || context.from) {
    writeStoredLaunchContext(context);
  }
  return context;
};

// ---------------------------------------------------------------------------
// Login redirect
// ---------------------------------------------------------------------------
const APP_HOST = "contextstory-app.yourrighthand.ai";

// The login host follows the host the app is served from, so an oelement.ai
// visitor never lands on the yourrighthand.ai login (or logout) page:
//   *.oelement.ai / *.oelement.net  -> https://login.oelement.ai
//   *.yourrighthand.ai              -> https://login.yourrighthand.ai
//   anything else (localhost)       -> REACT_APP_LOGIN_URL or login.oelement.ai
export const resolveLoginBase = (hostname = window.location.hostname) => {
  const h = String(hostname || "").toLowerCase();
  if (h === "oelement.ai" || h.endsWith(".oelement.ai") || h === "oelement.net" || h.endsWith(".oelement.net")) return "https://login.oelement.ai";
  if (h === "yourrighthand.ai" || h.endsWith(".yourrighthand.ai")) return "https://login.yourrighthand.ai";
  const raw = process.env.REACT_APP_LOGIN_URL || "https://login.oelement.ai";
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

export const resolveLoginUrl = ({ logout = false } = {}) => {
  const normalizedLoginBase = resolveLoginBase();
  const path = logout ? "/logout" : "/";
  const url = new URL(path, normalizedLoginBase);
  const context = persistLaunchContextFromLocation(window.location);
  const currentSource = normalizeRedirectSource(window.location.host || window.location.hostname || "");
  const sourceHost = normalizeRedirectSource(
    context.from || window.location.host || window.location.hostname || ""
  );

  if (context.tenant) url.searchParams.set("tenant", context.tenant);

  const fromHost = isLocalHost(currentSource) ? currentSource : context.from || sourceHost || APP_HOST;
  url.searchParams.set("from", isAllowedRedirectHost(fromHost) ? fromHost : APP_HOST);

  if (logout) url.searchParams.set("logout", "1");
  return url.toString();
};

export const redirectToLogin = () => {
  window.location.replace(resolveLoginUrl());
};

export const resolveChangePasswordUrl = () => {
  try {
    const target = new URL(resolveLoginUrl());
    target.searchParams.set("reset", "1");
    return target.toString();
  } catch (error) {
    console.error("Failed to build change password URL:", error);
    return `${resolveLoginBase()}/?reset=1`;
  }
};

export const redirectToChangePassword = () => {
  window.location.assign(resolveChangePasswordUrl());
};

// ---------------------------------------------------------------------------
// Token hydration from URL (returning from login app)
// ---------------------------------------------------------------------------
export const handleAuthRedirect = () => {
  if (typeof window === "undefined") return;

  const hashParams = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
  const queryParams = new URLSearchParams(window.location.search || "");

  const idToken = hashParams.get("id_token") || queryParams.get("id_token");
  if (!idToken) return;

  window.localStorage.setItem(AUTH_USER_TOKEN_KEY, idToken);

  const accessToken = hashParams.get("access_token") || queryParams.get("access_token");
  if (accessToken) {
    window.localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, accessToken);
  }

  const refreshToken = hashParams.get("refresh_token") || queryParams.get("refresh_token");
  if (refreshToken) {
    window.localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
  }

  const provider = hashParams.get("provider") || queryParams.get("provider");
  if (provider) {
    window.localStorage.setItem("oauth_provider", provider);
  }

  window.localStorage.setItem("LOGIN_ACTION", "1");

  // Clean sensitive params from URL, keep tenant/from
  const nextParams = new URLSearchParams(window.location.search || "");
  ["id_token", "access_token", "refresh_token", "token_type", "expires_in", "state", "auth"].forEach(
    (key) => nextParams.delete(key)
  );
  const queryString = nextParams.toString();
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + (queryString ? "?" + queryString : "")
  );
};

// ---------------------------------------------------------------------------
// Sign out
// ---------------------------------------------------------------------------
export const safeCognitoSignOut = async () => {
  const refreshToken = getStoredRefreshToken();
  if (refreshToken) {
    try {
      const cognitoDomain = process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
      const clientId = process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID;
      if (cognitoDomain && clientId) {
        const body = new URLSearchParams();
        body.set("token", refreshToken);
        body.set("client_id", clientId);
        await fetch(`https://${cognitoDomain}/oauth2/revoke`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
      }
    } catch (_error) {}
  }
  clearAuthStorage();
};

export const signOut = async () => {
  const targetUrl = resolveLoginUrl({ logout: true });
  try {
    await safeCognitoSignOut();
  } catch (_error) {
  } finally {
    window.location.assign(targetUrl);
  }
};
